import { QueryClient, useQuery } from "@tanstack/react-query";
import { GetOfficesResponse, OfficesAPI } from "./offices.api";

const officesQueryKeys = {
  list: () => ["offices", "list"],
  infiniteList: () => ["offices", "infinite-list"],
};

// Two observers mount on the office details page — the page itself and the
// delete modal — and the offices list changes rarely. Without a staleTime the
// second mount refetches immediately; a short window collapses that to one
// request. The delete guard re-reads the count itself before deleting, so a
// stale enabled/disabled state can never turn into a stale delete.
const OFFICES_LIST_STALE_TIME = 60 * 1000;

// Shares the ["offices","list"] cache with OfficesSection, OfficeSelector and
// the other list consumers, so callers that only need the office count
// (e.g. the last-office delete guard) usually read from cache.
export const useOfficesListQuery = () => {
  return useQuery<GetOfficesResponse>({
    queryKey: officesQueryKeys.list(),
    queryFn: () => OfficesAPI.get(),
    staleTime: OFFICES_LIST_STALE_TIME,
  });
};

// Forces a fresh read of the offices list and updates the shared cache with
// it. The delete guard uses this so it never decides off a count another user
// has already invalidated.
export const fetchOfficesList = (queryClient: QueryClient) =>
  queryClient.fetchQuery<GetOfficesResponse>({
    queryKey: officesQueryKeys.list(),
    queryFn: () => OfficesAPI.get(),
    staleTime: 0,
  });

export const OfficesQuery = {
  queryKeys: officesQueryKeys,
  useOfficesListQuery,
  fetchOfficesList,
};
